import Chip from "@mui/material/Chip";
import type { SxProps, Theme } from "@mui/material/styles";

/**
 * The owner + kind pair for a container wherever it is presented as a row or a
 * title (container-owner-chip 01/02). The owner leads as an outlined chip —
 * transparent background, raw label, omitted when blank — and the kind follows
 * as the filled chip. Keeping both here means every container list reads the
 * same, whatever feature opened it.
 *
 * Callers own the layout offset through `sx`, which applies to both chips, so a
 * row, a page header and a print secondary action can each place the pair
 * without re-declaring its styling.
 */
export function ContainerMetaChips({
  owner,
  kind,
  sx,
  ownerTestId,
  kindTestId,
}: {
  owner?: string;
  kind: string;
  /** Layout offset applied to both chips (margins, shrink, vertical alignment). */
  sx?: SxProps<Theme>;
  /** Optional test id for the owner chip; absent when the owner is blank. */
  ownerTestId?: string;
  /** Optional test id for the kind chip. */
  kindTestId?: string;
}) {
  const ownerLabel = owner?.trim();
  return (
    <>
      {ownerLabel ? (
        <Chip
          label={ownerLabel}
          size="small"
          variant="outlined"
          data-testid={ownerTestId}
          // A long owner truncates instead of widening the row (ticket 01).
          sx={[
            { maxWidth: 160, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
        />
      ) : null}
      <Chip label={kind} size="small" data-testid={kindTestId} sx={sx} />
    </>
  );
}
